import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, User } from "lucide-react";

interface EnrolledUsersModalProps {
  classId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface EnrolledUser {
  user_id: string;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    username: string | null;
  };
}

export const EnrolledUsersModal = ({ classId, isOpen, onClose }: EnrolledUsersModalProps) => {
  const { data: enrolledUsers, isLoading } = useQuery({
    queryKey: ["enrolledUsers", classId],
    queryFn: async () => {
      if (!classId) return [];
      
      const { data, error } = await supabase
        .from("class_subscriptions")
        .select(`
          user_id,
          profiles (
            first_name,
            last_name,
            username
          )
        `)
        .eq("class_id", classId);

      if (error) {
        console.error("Error fetching enrolled users:", error);
        throw error;
      }

      return data as EnrolledUser[];
    },
    enabled: !!classId,
  });

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Alumnos inscriptos</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : enrolledUsers?.length === 0 ? (
            <p className="text-center text-muted-foreground">
              No hay alumnos inscriptos en esta clase
            </p>
          ) : (
            <div className="space-y-2">
              {enrolledUsers?.map((subscription) => (
                <div
                  key={subscription.user_id}
                  className="flex items-center gap-3 p-3 bg-secondary rounded-lg"
                >
                  <User className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-secondary-foreground">
                      {subscription.profiles.first_name} {subscription.profiles.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {subscription.profiles.username}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};